import { AppliesToCard } from '@/app/considerations/applies-to/AppliesToCard';
import { PageHeader, PageShell } from '@/components/PageShell';
import RockDocRenderer from '@/components/docs/RockDocRenderer';
import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { ConsiderationCriticalityBadge } from '@/components/badges/ConsiderationCriticalityBadge';
import { DocumentStatusBadge } from '@/components/badges/DocumentStatusBadge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { lessonDocState, lessonTypeState, lessonsForTypeState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { LessonStatusLabels, type LessonType, type LessonTypeId } from '@rock/types';
import { formatDateTime } from '@/util/dates';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';

export const LessonTypeDetailsPage: FC = () => {
    const { typeId } = useParams<{ typeId?: LessonTypeId }>();
    const [lessonType, loadable] = useLoadableAtomValue(lessonTypeState(typeId));

    if (lessonType) return <LessonTypeDetailsContent lessonType={lessonType} />;
    // `useLoadableAtomValue` returns undefined while LOADING too, so not-found waits for the
    // resolved state — otherwise every hard refresh flashes "Lesson not found" first.
    if (loadable.state !== 'hasData') return null;
    return (
        <PageShell width="medium">
            <Card>
                <CardHeader>
                    <CardTitle>Lesson not found</CardTitle>
                    <CardDescription>No lesson type exists with this id.</CardDescription>
                </CardHeader>
            </Card>
        </PageShell>
    );
};

const LessonTypeDetailsContent: FC<{ lessonType: LessonType }> = ({ lessonType }) => {
    const onRowClick = useRowClick();
    const [doc, docLoadable] = useLoadableAtomValue(lessonDocState(lessonType.lessonDocId));
    // Already newest-first from the query's `orderBy('createdAt','desc')`, which the collection-group
    // index requires anyway — no client-side re-sort.
    const [lessons, lessonsLoadable] = useLoadableAtomValue(lessonsForTypeState(lessonType.id));
    const homes = lessons ?? [];

    return (
        <PageShell width="medium">
            <PageHeader title={lessonType.title} id={lessonType.id} backLabel="Back to lessons" />

            <Card>
                <CardHeader>
                    <CardTitle>Details</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-5 md:grid-cols-2">
                        <MetadataItem label="Space" value={lessonType.spaceId || '-'} />
                        <MetadataItem
                            label="Status"
                            value={<DocumentStatusBadge status={lessonType.documentStatus} />}
                        />
                        <MetadataItem
                            label="Criticality"
                            value={
                                lessonType.defaultCriticality ? (
                                    <ConsiderationCriticalityBadge criticality={lessonType.defaultCriticality} />
                                ) : (
                                    '-'
                                )
                            }
                        />
                        <MetadataItem label="Overview" value={lessonType.overview || '-'} />
                    </div>
                </CardContent>
            </Card>

            <AppliesToCard type={lessonType} />

            <Card>
                <CardHeader>
                    <CardTitle>Homes{lessonsLoadable.state === 'hasData' ? ` (${homes.length})` : ''}</CardTitle>
                    <CardDescription>
                        Every home that has been given this lesson. Empty while the lesson is unapproved, held back for
                        want of an approved query, matched by no home, still waiting on its space to be set up, or
                        simply not yet reached by an evaluation.
                    </CardDescription>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                    {lessonsLoadable.state === 'loading' ? (
                        <div className="section-description">Loading…</div>
                    ) : lessonsLoadable.state === 'hasError' ? (
                        // Never render a failed query as "nobody got it" — that is the exact wrong
                        // answer for the one question this page exists to answer. A missing index or
                        // rule surfaces here as FAILED_PRECONDITION / permission-denied.
                        <div className="text-sm text-destructive">
                            Could not load homes:{' '}
                            {lessonsLoadable.error instanceof Error
                                ? lessonsLoadable.error.message
                                : String(lessonsLoadable.error)}
                        </div>
                    ) : homes.length === 0 ? (
                        <div className="section-description">No homes have received this lesson.</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="min-w-[240px]">Home</TableHead>
                                    <TableHead className="min-w-[110px]">Status</TableHead>
                                    <TableHead className="min-w-[130px]">Weekend</TableHead>
                                    <TableHead className="min-w-[200px]">Given</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {homes.map((lesson) => (
                                    <TableRow
                                        key={lesson.id}
                                        className="cursor-pointer"
                                        onClick={onRowClick(`/directory/homes/${encodeURIComponent(lesson.homeId)}`)}
                                    >
                                        <TableCell>
                                            <IdWithCopy id={lesson.homeId} variant="muted" className="text-sm" />
                                        </TableCell>
                                        <TableCell>{LessonStatusLabels[lesson.status]}</TableCell>
                                        <TableCell>{lesson.reviewAt ?? '-'}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {formatDateTime(lesson.createdAt)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Content</CardTitle>
                    <CardDescription>The body synced from the Notion Lesson Documents database.</CardDescription>
                </CardHeader>
                <CardContent>
                    {doc ? (
                        <RockDocRenderer blocks={doc.blocks} />
                    ) : !lessonType.lessonDocId ? (
                        <div className="section-description">
                            No document linked. Flip Sync Flag to "Initiate Sync" on the Notion page.
                        </div>
                    ) : docLoadable.state === 'loading' ? (
                        <div className="section-description">Loading…</div>
                    ) : docLoadable.state === 'hasError' ? (
                        <div className="text-sm text-destructive">
                            Could not load the document body:{' '}
                            {docLoadable.error instanceof Error ? docLoadable.error.message : String(docLoadable.error)}
                        </div>
                    ) : (
                        <div className="section-description">
                            The type links document <code>{lessonType.lessonDocId}</code>, but no such doc exists.
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Object</CardTitle>
                </CardHeader>
                <CardContent>
                    <JsonViewer data={lessonType} />
                </CardContent>
            </Card>
        </PageShell>
    );
};
